/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import {
    CluMigrationContentSideMargin,
    CluMigrationInfoDto,
    CluMigrationSteps
} from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';
import { CluMigrationModalFooter } from 'src/luis/verticals/cluMigrationModal/components/CluMigrationModalFooter';

export type LuisMigrationModalReviewAndFinishProps = {
    cluMigrationInfoDto: CluMigrationInfoDto;
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
};

const messages = defineMessages({
    title: {
        id: 'cluMigration.reviewAndFinishScreen.title',
        defaultMessage: 'Review and finish'
    },
    description: {
        id: 'cluMigration.reviewAndFinishScreen.description',
        defaultMessage: "Review the applications and resource you'd like to migrate to."
    },
    azureSubscription: {
        id: 'cluMigration.reviewAndFinishScreen.azureSubscription',
        defaultMessage: 'Azure subscription'
    },
    languageResource: {
        id: 'cluMigration.reviewAndFinishScreen.languageResource',
        defaultMessage: 'Language resource'
    },
    luisApplications: {
        id: 'cluMigration.reviewAndFinishScreen.luisApplications',
        defaultMessage: 'LUIS applications to be migrated'
    }
});

const scrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { minHeight: '500px', height: '100%', position: 'relative' }
});

export const ReviewAndFinishScreen = observer((props: LuisMigrationModalReviewAndFinishProps) => {
    const { cluMigrationInfoDto, onSubmit, onDismiss, onPrevious } = props;
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const getSubscriptionNameById = (id: string) => rootStore.azureStore.subscriptionStoreHouse.findById(id)?.name;

    return (
        <Stack>
            <ScrollablePane styles={scrollablePaneStyles}>
                <Stack
                    styles={{ root: { height: '100%', overflowY: 'auto' } }}
                    tokens={{ childrenGap: 24, padding: `0 0 0 ${CluMigrationContentSideMargin}` }}>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(messages.title)}
                        </LuisText>
                        <LuisText>{localize(messages.description)}</LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.azureSubscription)}</LuisText>
                        <LuisText styles={{ root: { paddingLeft: '5px' } }}>
                            {getSubscriptionNameById(cluMigrationInfoDto.languageResource.subscriptionId)}
                        </LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.languageResource)}</LuisText>
                        <LuisText styles={{ root: { paddingLeft: '5px' } }}>{cluMigrationInfoDto.languageResource.name}</LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.luisApplications)}</LuisText>
                        <Stack>
                            {cluMigrationInfoDto.applications.map(app => (
                                <LuisText key={app.id} styles={{ root: { paddingLeft: '5px' } }}>
                                    {app.name} (v{app.selectedVersion})
                                </LuisText>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
            </ScrollablePane>
            <CluMigrationModalFooter
                onNext={onSubmit}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={CluMigrationSteps.ReviewAndFinish}
            />
        </Stack>
    );
});
